import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "kc.cookie-consent";

type Consent = "accepted" | "rejected";

const read = (): Consent | null => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "accepted" || v === "rejected" ? v : null;
  } catch {
    return null;
  }
};

const write = (v: Consent) => {
  try {
    localStorage.setItem(STORAGE_KEY, v);
  } catch {
    /* ignore */
  }
};

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Légère temporisation pour ne pas gêner le LCP
    const t = setTimeout(() => setOpen(read() === null), 800);
    return () => clearTimeout(t);
  }, []);

  if (!open) return null;

  const decide = (v: Consent) => {
    write(v);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Consentement aux cookies"
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-3xl rounded-md border border-border bg-background/95 p-5 shadow-xl backdrop-blur md:inset-x-auto md:left-1/2 md:right-auto md:-translate-x-1/2"
    >
      <div className="flex items-start gap-4">
        <div className="hidden size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:flex">
          <Cookie className="size-5" />
        </div>
        <div className="flex-1 text-sm text-foreground/80">
          <p className="font-medium text-foreground">Nous respectons votre vie privée.</p>
          <p className="mt-1">
            Nous utilisons des cookies pour mesurer l'audience et améliorer l'expérience. Vous
            pouvez accepter, refuser ou consulter notre{" "}
            <Link
              to="/politique-confidentialite"
              className="underline underline-offset-4 hover:text-foreground"
            >
              politique de confidentialité
            </Link>
            .
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => decide("accepted")}>
              Tout accepter
            </Button>
            <Button size="sm" variant="outline" onClick={() => decide("rejected")}>
              Refuser
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => decide("rejected")}
          aria-label="Fermer"
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
};
